import React from "react";
import { motion } from "framer-motion";
import { TID } from "@/lib/testIds";
import { Heart, MessageCircle, Repeat, Bell, MapPin, Trophy, Sparkles } from "lucide-react";
import { useT } from "@/lib/i18n";

const EASE = [0.22, 1, 0.36, 1];

const FEED_IMG =
    "https://customer-assets-rejwkqb3.emergentagent.net/job_watchpoint-hub-1/artifacts/zhe6vx2f_IMG_1098.webp";

const STAT_ICONS = [Heart, MessageCircle, Repeat, Bell];

export default function CivicNetwork() {
    const { t } = useT();
    const stats = t("civic.stats") || [];
    const levels = t("civic.levels") || [];
    return (
        <section
            id="rete"
            data-testid="sn-civic-network"
            className="relative bg-sn-black text-sn-silver py-28 md:py-44 overflow-hidden"
        >
            <div className="absolute inset-0 hero-radial opacity-70" />
            <div className="absolute inset-0 hero-grid opacity-50" />
            <div className="grain" />

            <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.4 }}
                    transition={{ duration: 0.9, ease: EASE }}
                    className="flex items-baseline gap-4 mb-14 md:mb-20"
                >
                    <span className="font-mono-e text-[11px] uppercase tracking-[0.3em] text-sn-blue">
                        {t("civic.eyebrow")}
                    </span>
                    <span className="hairline-dark flex-1" />
                    <span className="font-mono-e text-[11px] uppercase tracking-[0.3em] text-sn-fog">
                        {t("civic.tagline")}
                    </span>
                </motion.div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-14 items-start">
                    <div className="lg:col-span-7">
                        <motion.h2
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.4 }}
                            transition={{ duration: 0.9, ease: EASE }}
                            className="font-serif text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-[-0.02em]"
                        >
                            {t("civic.headline_a")}{" "}
                            <em className="italic text-sn-blue">
                                {t("civic.headline_b")}
                            </em>{" "}
                            {t("civic.headline_c")}
                        </motion.h2>

                        <motion.p
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.4 }}
                            transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
                            className="mt-8 max-w-2xl text-lg text-sn-fog leading-relaxed"
                        >
                            {t("civic.body_a")}{" "}
                            <span className="text-sn-silver">
                                {t("civic.body_b")}
                            </span>
                            {t("civic.body_c")}{" "}
                            <span className="text-sn-silver">
                                {t("civic.body_d")}
                            </span>{" "}
                            {t("civic.body_e")}{" "}
                            <span className="text-sn-blue italic">
                                {t("civic.body_f")}
                            </span>
                            .
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.9, delay: 0.2 }}
                            className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4"
                        >
                            {stats.map((s, idx) => {
                                const Icon = STAT_ICONS[idx] || Heart;
                                return (
                                    <div
                                        key={idx}
                                        className="border border-white/10 rounded-md p-4 bg-white/[0.02]"
                                    >
                                        <Icon size={16} className="text-sn-blue mb-3" />
                                        <div className="font-serif text-3xl text-sn-silver">
                                            {s.v}
                                        </div>
                                        <div className="font-mono-e text-[9px] tracking-widest text-sn-fog uppercase mt-1">
                                            {s.k}
                                        </div>
                                    </div>
                                );
                            })}
                        </motion.div>

                        <div className="mt-14">
                            <div className="flex items-center gap-3 mb-6">
                                <Trophy size={16} className="text-sn-blue" />
                                <span className="font-mono-e text-[11px] uppercase tracking-[0.28em] text-sn-fog">
                                    {t("civic.levelsTitle")}
                                </span>
                            </div>
                            <ol className="divide-y divide-white/10 border-t border-white/10">
                                {levels.map((l, i) => (
                                    <motion.li
                                        key={i}
                                        initial={{ opacity: 0, x: -20 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        viewport={{ once: true, amount: 0.3 }}
                                        transition={{ duration: 0.6, delay: i * 0.08, ease: EASE }}
                                        className="grid grid-cols-12 gap-4 items-baseline py-5"
                                    >
                                        <span className="col-span-2 font-mono-e text-xs tracking-widest text-sn-blue">
                                            {l.n}
                                        </span>
                                        <span className="col-span-4 font-serif text-2xl md:text-3xl italic">
                                            {l.title}
                                        </span>
                                        <span className="col-span-6 text-sm text-sn-fog">
                                            {l.desc}
                                        </span>
                                    </motion.li>
                                ))}
                            </ol>
                        </div>
                    </div>

                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ duration: 1, ease: EASE }}
                        className="lg:col-span-5 relative flex justify-center lg:justify-end"
                    >
                        <div className="relative w-[300px] md:w-[340px] aspect-[9/19]">
                            <div className="absolute -inset-14 rounded-[3rem] bg-[radial-gradient(ellipse_at_center,rgba(30,107,255,0.35),transparent_70%)] blur-3xl" />
                            <div className="relative w-full h-full rounded-[2.6rem] bg-black border border-white/10 phone-shadow overflow-hidden">
                                <div className="absolute top-2 left-1/2 -translate-x-1/2 h-5 w-24 bg-black rounded-full z-30" />
                                <div className="absolute inset-2 rounded-[2.2rem] overflow-hidden">
                                    <img
                                        src={FEED_IMG}
                                        alt="Sentinel Feed"
                                        className="w-full h-full object-cover"
                                    />
                                </div>
                            </div>

                            <motion.div
                                initial={{ opacity: 0, x: 40, y: -20 }}
                                whileInView={{ opacity: 1, x: 0, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.9, delay: 0.4, ease: EASE }}
                                className="absolute -left-6 md:-left-16 top-24 max-w-[220px] bg-[#0F0F14] border border-sn-blue/40 rounded-lg p-3 shadow-2xl"
                            >
                                <div className="flex items-center gap-2 font-mono-e text-[9px] tracking-widest text-sn-blue uppercase">
                                    <Bell size={12} /> {t("civic.floatNotifTitle")}
                                </div>
                                <div className="mt-1 text-sm text-sn-silver">
                                    {t("civic.floatNotifText")}
                                </div>
                                <div className="mt-2 flex items-center gap-2 font-mono-e text-[9px] tracking-widest text-sn-fog uppercase">
                                    <MapPin size={10} /> {t("civic.floatNotifLoc")}
                                </div>
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0, x: -40, y: 20 }}
                                whileInView={{ opacity: 1, x: 0, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.9, delay: 0.55, ease: EASE }}
                                className="absolute -right-4 md:-right-14 bottom-32 max-w-[210px] bg-[#0F0F14] border border-white/15 rounded-lg p-3 shadow-2xl"
                            >
                                <div className="flex items-center gap-2 font-mono-e text-[9px] tracking-widest text-sn-fog uppercase">
                                    <Sparkles size={12} className="text-sn-blue" />{" "}
                                    {t("civic.floatAiTitle")}
                                </div>
                                <div className="mt-1 font-serif text-2xl text-sn-silver">
                                    € 601
                                </div>
                                <div className="mt-1 font-mono-e text-[9px] tracking-widest text-sn-blue uppercase">
                                    {t("civic.floatAiBadge")}
                                </div>
                            </motion.div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
